import api from './api';

export interface AppUpdatePlatformPolicy {
  minSupportedBuild: number;
  forceUpdateBelowBuild: number;
  latestProductionBuild: number;
  latestProductionVersion: string;
  updateUrl: string;
  message: string;
}

export interface AppUpdateRuntimeConfig {
  android: AppUpdatePlatformPolicy;
  ios: AppUpdatePlatformPolicy;
  updatedAt?: unknown;
  updatedBy?: string | null;
  sources?: {
    persisted: {
      android: Partial<AppUpdatePlatformPolicy>;
      ios: Partial<AppUpdatePlatformPolicy>;
    };
    env: {
      android: AppUpdatePlatformPolicy;
      ios: AppUpdatePlatformPolicy;
    };
  };
}

export interface UpdateAppUpdatePolicyPayload {
  // Omit a field to preserve its override; null restores its environment fallback.
  android?: { [K in keyof AppUpdatePlatformPolicy]?: AppUpdatePlatformPolicy[K] | null } | null;
  ios?: { [K in keyof AppUpdatePlatformPolicy]?: AppUpdatePlatformPolicy[K] | null } | null;
}

export const getAppUpdatePolicy = async (
  env: 'dev' | 'prod' = 'dev'
): Promise<AppUpdateRuntimeConfig> => {
  const response = await api.get('/api/connect-config/app-update-policy', {
    params: { env },
  });
  return response.data.data;
};

export const updateAppUpdatePolicy = async ({
  payload,
  env = 'dev',
}: {
  payload: UpdateAppUpdatePolicyPayload;
  env?: 'dev' | 'prod';
}): Promise<AppUpdateRuntimeConfig> => {
  const response = await api.put('/api/connect-config/app-update-policy', payload, {
    params: { env },
  });
  return response.data.data;
};

export interface AppVersionCounts {
  devices: number;
  users: number;
}

export interface AppVersionBuild {
  buildNumber: number;
  appVersion: string | null;
  active: AppVersionCounts;
  inactive: AppVersionCounts;
}

export interface AppVersionPlatformSummary {
  builds: AppVersionBuild[];
  /** Devices on builds from before the app reported its build number. */
  unreported: { active: AppVersionCounts; inactive: AppVersionCounts };
  oldestActiveBuild: number | null;
}

/** Devices per build; active means signed in and seen within activeWindowDays. */
export interface AppVersionSummary {
  activeWindowDays: number;
  generatedAt: string;
  platforms: Partial<Record<'android' | 'ios' | 'unknown', AppVersionPlatformSummary>>;
}

export const getAppVersions = async (
  env: 'dev' | 'prod' = 'dev'
): Promise<AppVersionSummary> => {
  const response = await api.get('/api/connect-config/app-versions', {
    params: { env },
  });
  return response.data.data;
};

/** One counted method/path/status/build row within a surface. */
export interface CompatibilityUsageRoute {
  method: string;
  path: string;
  statusClass: string;
  build: string | null;
  requests: number;
  lastSeenDay: string;
}

export interface CompatibilityUsageSurface {
  surface: string;
  requests: number;
  daysWithTraffic: number;
  lastSeenDay: string | null;
  distinctUsers: number | null;
  distinctUsersIsLowerBound: boolean;
  trackedRoutes: string[] | null;
  routes: CompatibilityUsageRoute[];
}

export interface LinkCleanupDay {
  day: string;
  scheduledRuns: number;
  dryRuns: number;
  runsWithErrors: number;
  scannedSessions: number;
  deletedSessions: number;
  failedSessions: number;
  averageDurationMs: number | null;
}

export interface LinkCleanupTotals {
  scheduledRuns: number;
  dryRuns: number;
  runsWithErrors: number;
  scannedSessions: number;
  deletedSessions: number;
  failedSessions: number;
  averageDurationMs: number | null;
}

/**
 * Old-route call evidence plus expired-link cleanup totals. Counts only —
 * the backend never returns user IDs, tokens or payloads, so there is
 * nothing per-user for this UI to leak or log.
 */
export interface CompatibilityUsageReport {
  days: number;
  since: string;
  generatedAt: string;
  surfaces: CompatibilityUsageSurface[];
  linkCleanup: {
    days: LinkCleanupDay[];
    totals: LinkCleanupTotals;
  };
}

export const getCompatibilityUsage = async (
  env: 'dev' | 'prod' = 'dev',
  days: number = 30
): Promise<CompatibilityUsageReport> => {
  const response = await api.get('/api/connect-config/compatibility-usage', {
    params: { env, days },
  });
  return response.data.data;
};
